/* ===========================================================================
   hn-a11y.js — display preferences for hydronate.com
   ---------------------------------------------------------------------------
   This is NOT an accessibility overlay. It does not claim to fix the site's
   markup and it does not touch anything a screen reader consumes. It changes
   how the page LOOKS in this browser, on this device, and says so out loud in
   the panel. The real remediation work lives in the theme source.

   WHY TEXT SIZE IS DONE IN JAVASCRIPT AND NOT CSS
   Every HN section hard-codes its font sizes in px inside inline style
   attributes ("font:450 13.5px/1.6 'Manrope'"). px ignores the root font-size,
   so the usual `html{font-size:115%}` trick moves nothing at all here. The
   honest options were (a) `zoom`, which scales photographs too and is really
   just browser zoom in a costume, or (b) walk the DOM and re-set each computed
   px size. (b) is what WCAG 1.4.4 is actually asking for — text that resizes
   while the images stay put — so that is what this does. The original computed
   size is cached in a WeakMap on first pass, so repeated toggling never
   compounds and Reset restores exactly what was there.

   TRI-STATE PREFS: motion and contrast are 0 / 1 / undefined. undefined means
   "no opinion, let the OS decide" and the CSS @media blocks handle it. An
   explicit 0 means the visitor overrode their OS setting, which the CSS
   respects via html:not([data-hn-motion="0"]).
   =========================================================================== */
(function () {
  'use strict';

  var KEY = 'hnA11y.v1';
  var FLAGS = ['spacing', 'links', 'dark', 'focus', 'guide', 'read'];  // plain on/off
  var TRI = ['contrast', 'motion'];                                     // 0/1/undefined
  var MULT = [1, 1.15, 1.3];

  var panel, live, osNote;

  /* Anything that opens the panel. There is no dedicated launcher button any
     more -- the entry point is the "Accessibility" link in the bottom footer
     legal bar, which is a settings-driven <a href="/pages/accessibility"> in
     every one of the 17 templates. Binding it by URL rather than by editing
     all 17 keeps this to one place. data-hn-a11y-open is the explicit escape
     hatch if a future trigger should not point at that URL. */
  var OPEN_SEL = '[data-hn-a11y-open],a[href*="/pages/accessibility"]';
  var lastOpener = null;

  function openerFor(node) {
    if (!node || !node.closest) return null;
    var t = node.closest(OPEN_SEL);
    if (!t) return null;
    // The panel's OWN "Accessibility statement" link points at the same URL.
    // It must stay a real navigation, or the one route to the statement page
    // becomes a button that closes the thing you are reading it from.
    if (t.closest('[data-hn-a11y]')) return null;
    return t;
  }
  var baseSize = new WeakMap();
  var scaled = false;
  var observer = null;

  /* ---------------------------------------------------------------- storage */
  function load() {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function save(p) {
    try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {}
  }

  /* ------------------------------------------------------------ attributes */
  function reflect(p) {
    var d = document.documentElement;
    FLAGS.forEach(function (k) {
      if (p[k]) d.setAttribute('data-hn-' + k, '1');
      else d.removeAttribute('data-hn-' + k);
    });
    TRI.forEach(function (k) {
      if (p[k] === 1) d.setAttribute('data-hn-' + k, '1');
      else if (p[k] === 0) d.setAttribute('data-hn-' + k, '0');
      else d.removeAttribute('data-hn-' + k);
    });
    if (p.text) d.setAttribute('data-hn-text', String(p.text));
    else d.removeAttribute('data-hn-text');
  }

  /* ------------------------------------------------------------- text size */
  /* px font sizes ignore the root font-size, and every HN section sets its
     sizes in inline style attributes, so this cannot be done in CSS. It has to
     be a DOM pass -- and the pass has to MEASURE EVERYTHING BEFORE IT WRITES
     ANYTHING. querySelectorAll('*') is document order: write as you walk and a
     child that inherits its size gets measured after its parent already grew,
     compounding once per scaled ancestor. That shipped an 18px nav link at
     66.83px (= 18 x 1.3^5) while a 60px h1, which declares its own size and so
     is immune to inheritance, scaled correctly to 78px. */

  function collect(root) {
    var nodes = root.querySelectorAll('*'), out = [], i, el, tag, n = nodes.length;
    for (i = -1; i < n; i++) {
      el = i < 0 ? root : nodes[i];
      if (!el || el.nodeType !== 1) continue;
      tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'BR' ||
          tag === 'svg' || tag === 'SVG' || tag === 'PATH' || tag === 'path') continue;
      if (el.closest && el.closest('[data-hn-a11y]')) continue;   // never restyle the panel itself
      out.push(el);
    }
    return out;
  }

  function restoreAll() {
    var els = collect(document.body), i, rec;
    for (i = 0; i < els.length; i++) {
      rec = baseSize.get(els[i]);
      if (rec) els[i].style.fontSize = rec.inline;
    }
  }

  function scaleTree(m) {
    var els = collect(document.body), fresh = [], i, el, rec;

    for (i = 0; i < els.length; i++) if (!baseSize.has(els[i])) fresh.push(els[i]);

    if (fresh.length) {
      // Measure at TRUE base. If the page is currently scaled, these new nodes
      // inherit the scaled value, so drop everything back first.
      if (scaled) restoreAll();
      for (i = 0; i < fresh.length; i++) {          // reads only -- no interleaved writes
        el = fresh[i];
        baseSize.set(el, {
          inline: el.style.fontSize,
          px: parseFloat(getComputedStyle(el).fontSize) || 0
        });
      }
    }

    for (i = 0; i < els.length; i++) {              // writes only
      el = els[i];
      rec = baseSize.get(el);
      if (!rec || !rec.px) continue;
      el.style.fontSize = m === 1 ? rec.inline : (rec.px * m).toFixed(2) + 'px';
    }
  }

  function scaleText(step) {
    var m = MULT[step] || 1;
    if (m === 1 && !scaled) return;          // nothing was ever touched, skip the pass
    if (!document.body) return;
    scaleTree(m);
    scaled = m !== 1;
    if (scaled && !observer && window.MutationObserver) {
      // Lazy sections, the cart drawer and app-injected markup all arrive after
      // load. Without this they would be the only unscaled text on the page.
      // childList only -- watching attributes would re-fire on our own writes.
      var pending = false;
      observer = new MutationObserver(function () {
        var cur = MULT[load().text] || 1;
        if (cur === 1 || pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; scaleTree(cur); });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* --------------------------------------------------------- reading guide */
  function guideMove(e) {
    var g = document.getElementById('hn-a11y-guide');
    if (g) g.style.top = Math.max(0, e.clientY - 23) + 'px';
  }
  function guideFocus(e) {
    var g = document.getElementById('hn-a11y-guide');
    if (!g || !e.target || !e.target.getBoundingClientRect) return;
    var r = e.target.getBoundingClientRect();
    g.style.top = Math.max(0, r.top + r.height / 2 - 23) + 'px';
  }
  function guide(on) {
    var g = document.getElementById('hn-a11y-guide');
    if (!on) {
      if (g) g.parentNode.removeChild(g);
      document.removeEventListener('pointermove', guideMove);
      document.removeEventListener('focusin', guideFocus);
      return;
    }
    if (!g) {
      g = document.createElement('div');
      g.id = 'hn-a11y-guide';
      g.setAttribute('aria-hidden', 'true');
      g.style.top = (window.innerHeight / 2 - 23) + 'px';
      document.body.appendChild(g);
    }
    document.addEventListener('pointermove', guideMove);
    document.addEventListener('focusin', guideFocus);   // so it works keyboard-only too
  }

  /* ------------------------------------------------------------ reduce motion */
  function calmVideos(on) {
    if (!on) return;
    var v = document.querySelectorAll('video[autoplay]');
    for (var i = 0; i < v.length; i++) {
      try { v[i].pause(); v[i].removeAttribute('autoplay'); v[i].controls = true; } catch (e) {}
    }
  }

  /* -------------------------------------------------------------- announce */
  function say(msg) { if (live) live.textContent = msg; }

  /* ----------------------------------------------------------------- apply */
  function apply(p, announce) {
    reflect(p);
    scaleText(p.text || 0);
    guide(!!p.guide);
    calmVideos(p.motion === 1 ||
      (p.motion === undefined && window.matchMedia &&
       matchMedia('(prefers-reduced-motion: reduce)').matches));
    if (announce) say(announce);
  }

  /* ------------------------------------------------------------------ open */
  function isOpen() { return panel && !panel.hidden; }
  function open(trigger) {
    if (trigger) lastOpener = trigger;
    panel.hidden = false;
    if (lastOpener) lastOpener.setAttribute('aria-expanded', 'true');
    var first = panel.querySelector('input,button,a');
    if (first) first.focus();
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onOutside, true);
  }
  function close(refocus) {
    panel.hidden = true;
    if (lastOpener) lastOpener.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKey, true);
    document.removeEventListener('pointerdown', onOutside, true);
    // Send focus back to the footer link that opened the panel. Without this
    // the caret lands back at the top of the document and a keyboard user has
    // to tab the whole page again to get back to where they were.
    if (refocus !== false && lastOpener && document.contains(lastOpener)) lastOpener.focus();
  }
  function onKey(e) { if (e.key === 'Escape' && isOpen()) { e.stopPropagation(); close(); } }
  function onOutside(e) {
    if (!isOpen()) return;
    if (panel.contains(e.target)) return;
    if (openerFor(e.target)) return;   // the click handler will toggle it
    close(false);   // clicked the page on purpose; do not steal focus back
  }

  /* ------------------------------------------------------------------ init */
  function init() {
    var wrap = document.querySelector('[data-hn-a11y]');
    if (!wrap) return;
    panel = document.getElementById('hn-a11y-panel');
    live = wrap.querySelector('[data-hn-a11y-live]');
    osNote = wrap.querySelector('[data-hn-a11y-os]');
    if (!panel) return;

    var prefs = load();

    // Reflect saved state (and, where there is none, the OS's opinion) in the
    // controls, so the panel never lies about what is currently switched on.
    var mqMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)');
    var mqContrast = window.matchMedia && matchMedia('(prefers-contrast: more)');
    var osAsks = (mqMotion && mqMotion.matches) || (mqContrast && mqContrast.matches);
    if (osAsks && osNote) osNote.hidden = false;

    wrap.querySelectorAll('input[type="checkbox"][data-pref]').forEach(function (cb) {
      var k = cb.getAttribute('data-pref');
      if (k === 'motion') cb.checked = prefs.motion === 1 || (prefs.motion === undefined && mqMotion && mqMotion.matches);
      else if (k === 'contrast') cb.checked = prefs.contrast === 1 || (prefs.contrast === undefined && mqContrast && mqContrast.matches);
      else cb.checked = !!prefs[k];
      cb.addEventListener('change', function () {
        var p = load();
        if (k === 'motion' || k === 'contrast') p[k] = cb.checked ? 1 : 0;
        else if (cb.checked) p[k] = 1; else delete p[k];
        save(p);
        apply(p, cb.parentNode.querySelector('span').textContent + (cb.checked ? ' on' : ' off'));
      });
    });

    wrap.querySelectorAll('input[name="hn-a11y-text"]').forEach(function (r) {
      r.checked = String(prefs.text || 0) === r.value;
      r.addEventListener('change', function () {
        var p = load();
        if (r.value === '0') delete p.text; else p.text = +r.value;
        save(p);
        apply(p, 'Text size: ' + r.getAttribute('data-label'));
      });
    });

    // Tell assistive tech that the footer link opens a dialog rather than
    // navigating. Done here and not in the footer markup because the link is a
    // theme-editor "legal" block shared by every template.
    document.querySelectorAll(OPEN_SEL).forEach(function (t) {
      if (t.closest('[data-hn-a11y]')) return;
      t.setAttribute('aria-haspopup', 'dialog');
      t.setAttribute('aria-controls', 'hn-a11y-panel');
      t.setAttribute('aria-expanded', 'false');
    });

    // Delegated, so a footer rendered later by a section re-render still works.
    document.addEventListener('click', function (e) {
      // Let the browser do its normal thing for middle-click and for
      // cmd/ctrl/shift-click: someone deliberately opening the accessibility
      // statement in a new tab should get the statement, not a dialog.
      if (e.defaultPrevented || e.button > 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var t = openerFor(e.target);
      if (!t) return;
      // preventDefault only once we know we are handling it, so a broken script
      // leaves the link navigating to the statement page instead of dead.
      e.preventDefault();
      (isOpen() && lastOpener === t) ? close() : open(t);
    }, false);
    wrap.querySelectorAll('[data-hn-a11y-close]').forEach(function (b) {
      b.addEventListener('click', function () { close(); });
    });
    var reset = wrap.querySelector('[data-hn-a11y-reset]');
    if (reset) reset.addEventListener('click', function () {
      try { localStorage.removeItem(KEY); } catch (e) {}
      wrap.querySelectorAll('input[type="checkbox"][data-pref]').forEach(function (cb) {
        var k = cb.getAttribute('data-pref');
        cb.checked = (k === 'motion' && mqMotion && mqMotion.matches) ||
                     (k === 'contrast' && mqContrast && mqContrast.matches);
      });
      wrap.querySelectorAll('input[name="hn-a11y-text"]').forEach(function (r) { r.checked = r.value === '0'; });
      apply({}, 'All display preferences reset');
    });

    apply(prefs);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
